import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Train, Loader2, KeyRound, PlugZap, Unlock, Eye, EyeOff, CheckCircle2, XCircle, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import {
  getFerrovalleCredentials,
  revealFerrovallePassword,
  setFerrovalleCredentials,
  testFerrovalleLogin,
  resetFerrovalleLockout,
} from '../lib/api';

export default function FerrovalleCredentialsPanel() {
  const [status, setStatus] = useState(null);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [version, setVersion] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activePassword, setActivePassword] = useState(null);
  const [revealing, setRevealing] = useState(false);

  const handleReveal = async () => {
    if (activePassword !== null) { setActivePassword(null); return; }
    setRevealing(true);
    try {
      const res = await revealFerrovallePassword();
      setActivePassword(res.data?.password ?? '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo obtener la contraseña activa');
    } finally {
      setRevealing(false);
    }
  };

  const load = async () => {
    try {
      const res = await getFerrovalleCredentials();
      setStatus(res.data);
      if (res.data?.user) setUser(res.data.user);
      if (res.data?.version) setVersion(res.data.version);
    } catch (err) {
      // silent — panel still usable for first-time setup
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!user.trim() || !password) {
      toast.error('Usuario y contraseña son obligatorios');
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      await setFerrovalleCredentials({ user: user.trim(), password, version: version.trim() || undefined });
      toast.success('Credenciales guardadas', { description: 'Ahora prueba el login para validar.' });
      setPassword('');
      setActivePassword(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron guardar las credenciales');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    const tid = toast.loading('Probando login con Ferrovalle… (~15s)');
    try {
      const res = await testFerrovalleLogin();
      setTestResult(res.data);
      await load();
      if (res.data?.ok) {
        toast.success('Login exitoso', { id: tid, description: `Conectado como ${res.data.user_name || user}` });
      } else {
        toast.error('Login falló', { id: tid, description: res.data?.error || 'Sin mensaje' });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al probar login', { id: tid });
    } finally {
      setTesting(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await resetFerrovalleLockout();
      toast.success('Bloqueo (circuit breaker) reseteado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo resetear el bloqueo');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="bg-secondary/30 px-4 py-3 border-t border-border" data-testid="ferrovalle-credentials-section">
      <p className="text-sm font-medium flex items-center gap-1.5">
        <Train size={14} className="text-blue-400" />
        Credenciales Ferrovalle SLI
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">
        Se guardan cifradas en la base de datos (no en variables de entorno), así puedes rotar el password
        sin re-deploy y sin que se manglen caracteres especiales como <code>$</code>, <code>#</code> o espacios.
      </p>

      {status && (
        <p className="text-xs mt-1.5 text-muted-foreground" data-testid="ferrovalle-creds-status">
          Fuente actual: <strong className={
            status.source === 'db' ? 'text-emerald-400'
            : status.source === 'db_decrypt_error' ? 'text-red-400'
            : 'text-amber-400'
          }>
            {status.source === 'db' ? 'Base de datos (UI)'
             : status.source === 'db_decrypt_error' ? '⚠ Error al descifrar — vuelve a guardar la contraseña'
             : 'Variable de entorno'}
          </strong>
          {status.updated_at && ` · Actualizado por ${status.updated_by || '—'}`}
        </p>
      )}

      {status?.validity && (
        <div className="mt-2" data-testid="ferrovalle-validity-badge">
          {status.validity.status === 'vigente' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              <ShieldCheck size={13} /> Contraseña vigente
              {status.validity.last_success_at && ` · último login ${new Date(status.validity.last_success_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}`}
            </span>
          )}
          {status.validity.status === 'requiere_cambio' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">
              <ShieldAlert size={13} /> Requiere cambio / inválida
            </span>
          )}
          {status.validity.status === 'desconocido' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-muted text-muted-foreground border border-border">
              <ShieldQuestion size={13} /> Sin verificar — usa «Probar login»
            </span>
          )}
          {status.validity.status === 'requiere_cambio' && status.validity.reason && (
            <p className="text-xs text-red-300/80 mt-1 break-words">{status.validity.reason}</p>
          )}
        </div>
      )}

      {status?.has_password && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="ferrovalle-active-password-row">
          <span className="text-xs text-muted-foreground">Contraseña activa:</span>
          <code className="text-xs bg-background px-2 py-1 rounded border border-border font-mono">
            {activePassword === null ? '••••••••' : (activePassword || '(vacía)')}
          </code>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={handleReveal}
            disabled={revealing}
            data-testid="ferrovalle-reveal-password-btn"
          >
            {revealing ? <Loader2 size={13} className="animate-spin mr-1" />
              : activePassword === null ? <Eye size={13} className="mr-1" /> : <EyeOff size={13} className="mr-1" />}
            {activePassword === null ? 'Ver' : 'Ocultar'}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div>
          <Label className="text-xs">Usuario</Label>
          <Input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="sistema@myofimex.com"
            className="h-9 mt-1"
            data-testid="ferrovalle-user-input"
          />
        </div>
        <div>
          <Label className="text-xs">Contraseña nueva</Label>
          <div className="relative mt-1">
            <Input
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-9 pr-9"
              data-testid="ferrovalle-pass-input"
            />
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs">Versión (opcional)</Label>
          <Input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="1.038"
            className="h-9 mt-1 font-mono"
            data-testid="ferrovalle-version-input"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="ferrovalle-save-creds-btn">
          {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <KeyRound size={14} className="mr-1.5" />}
          Guardar credenciales
        </Button>
        <Button size="sm" variant="outline" onClick={handleTest} disabled={testing} data-testid="ferrovalle-test-login-btn">
          {testing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <PlugZap size={14} className="mr-1.5" />}
          Probar login ahora
        </Button>
        <Button size="sm" variant="ghost" onClick={handleReset} disabled={resetting} data-testid="ferrovalle-reset-lockout-btn">
          {resetting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Unlock size={14} className="mr-1.5" />}
          Desbloquear
        </Button>
      </div>

      {testResult && (
        <div
          className={`mt-3 px-3 py-2 rounded-sm text-xs flex items-start gap-2 ${
            testResult.ok ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                          : 'bg-red-500/10 text-red-300 border border-red-500/30'
          }`}
          data-testid="ferrovalle-test-result"
        >
          {testResult.ok ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <XCircle size={14} className="shrink-0 mt-0.5" />}
          <span className="break-words">
            {testResult.ok
              ? `Login exitoso como ${testResult.user_name || user} (${testResult.user_id || '—'}).`
              : `Falló: ${testResult.error}`}
          </span>
        </div>
      )}
    </div>
  );
}
